import { Link, type LinkProps } from "react-router-dom"
import type { ButtonHTMLAttributes, ReactNode } from "react"

const cn = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(" ")

export type ButtonLinkHeaderTone = "solid" | "transparent" | "onAccent"

export type ButtonLinkVariant =
  | "navHeader"
  | "navMobile"
  | "navMobileAccent"
  | "navPillOutline"
  | "footerText"
  | "portalNav"
  | "portalNavLogout"
  | "ctaPrimary"
  | "ctaOutline"
  | "ctaOutlineCompact"
  /** Квадратная кнопка 40×40 с брендовой обводкой — тот же силуэт, что у `navPillOutline` + `pillSize="iconAlways"` на светлом хедере */
  | "ctaOutlineIcon"
  | "ctaOutlineSm"
  | "ctaOutlineNeutralToolbar"
  /** Сплошная кнопка для финального подтверждения разрушающего действия (удалить в модалке и т.п.). Не путать с брендовым `ctaPrimary`. */
  | "ctaDanger"
  /** Компактная кнопка в строке таблицы: удалить / опасное вторичное действие (обводка и фон в красной гамме). */
  | "ctaDangerOutline"
  | "ctaOnDark"
  | "linkSubtle"

type BaseProps = {
  children: ReactNode
  className?: string
  variant: ButtonLinkVariant
  headerTone?: ButtonLinkHeaderTone
  /** For navHeader, navMobile, portalNav */
  isActive?: boolean
  /** For navPillOutline: текстовая кнопка в хедере или квадрат 40×40 с иконкой (`icon` — только узкая ширина; `iconAlways` — всегда) */
  pillSize?: "default" | "defaultAlways" | "icon" | "iconAlways"
}

type ButtonLinkAsRouterLink = BaseProps &
  Omit<LinkProps, "className" | "children"> & {
    to: string
  }

type ButtonLinkAsNativeButton = BaseProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, "className" | "children"> & {
    to?: undefined
  }

export type ButtonLinkProps = ButtonLinkAsRouterLink | ButtonLinkAsNativeButton

/** Вариант 2: текст + нижняя линия (::after), на hover у неактивных линия слегка проявляется */
const navHeaderUnderlineBase =
  "relative inline-flex items-center justify-center px-2 sm:px-2.5 lg:px-3 py-2 font-nav text-[15px] font-semibold tracking-wide rounded-md whitespace-nowrap transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 after:content-[''] after:pointer-events-none after:absolute after:bottom-0.5 after:left-2 after:right-2 after:h-0.5 after:rounded-full after:bg-brand-500 after:origin-center motion-safe:after:transition-[transform,opacity] motion-safe:after:duration-200"

const navHeaderActive =
  "font-semibold text-brand-500 after:scale-x-100 after:opacity-100 focus-visible:ring-offset-2"

const navHeaderTransparentInactive =
  "text-white font-semibold drop-shadow hover:text-white after:scale-x-0 after:opacity-0 hover:after:scale-x-90 hover:after:opacity-40 focus-visible:ring-white focus-visible:ring-offset-0 focus-visible:ring-offset-transparent"

const navHeaderSolidInactive =
  "text-gray-700 hover:text-gray-900 after:scale-x-0 after:opacity-0 hover:after:scale-x-90 hover:after:opacity-40"

const pillOutlineDefault =
  "border-brand-500 text-brand-500 hover:bg-brand-500 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
const pillOutlineActive =
  "border-brand-500 bg-brand-500 text-white shadow-sm hover:bg-brand-700 hover:border-brand-700 hover:text-white focus-visible:ring-brand-500"
const pillOutlineOnAccent =
  "border-white/70 text-white hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-0"
const pillOutlineOnAccentActive =
  "border-white bg-white/20 text-white hover:bg-white/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-0"

const brandIconButtonShell =
  "inline-flex items-center justify-center w-10 h-10 rounded-lg border transition-all duration-200 shrink-0"

const resolveClasses = ({
  variant,
  headerTone = "solid",
  isActive = false,
  pillSize = "default",
}: Pick<BaseProps, "variant" | "headerTone" | "isActive" | "pillSize">) => {
  if (variant === "navHeader") {
    if (isActive) {
      return cn(navHeaderUnderlineBase, navHeaderActive)
    }
    if (headerTone === "transparent") {
      return cn(navHeaderUnderlineBase, navHeaderTransparentInactive)
    }
    return cn(navHeaderUnderlineBase, navHeaderSolidInactive)
  }

  if (variant === "navMobile") {
    const navMobileUnderlineBase =
      "relative block w-full py-2 text-left font-nav text-[15px] font-semibold tracking-wide rounded-md transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 after:content-[''] after:pointer-events-none after:absolute after:bottom-1 after:left-0 after:right-0 after:h-0.5 after:rounded-full after:bg-brand-500 after:origin-center motion-safe:after:transition-[transform,opacity] motion-safe:after:duration-200"
    return cn(
      navMobileUnderlineBase,
      isActive
        ? "font-semibold text-brand-500 after:scale-x-100 after:opacity-100"
        : "text-gray-700 after:scale-x-0 after:opacity-0 hover:after:scale-x-95 hover:after:opacity-40",
    )
  }

  if (variant === "navMobileAccent") {
    return cn(
      "mt-2 py-2 text-sm font-medium text-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 rounded-md",
    )
  }

  if (variant === "navPillOutline") {
    const tone =
      headerTone === "onAccent"
        ? isActive
          ? pillOutlineOnAccentActive
          : pillOutlineOnAccent
        : isActive
          ? pillOutlineActive
          : pillOutlineDefault
    if (pillSize === "icon") {
      return cn(brandIconButtonShell, "md:hidden", tone)
    }
    if (pillSize === "iconAlways") {
      return cn(brandIconButtonShell, tone)
    }
    const pillTextShell =
      "inline-flex items-center gap-1.5 sm:gap-2 px-3 lg:px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold border transition-all duration-200 whitespace-nowrap shrink-0"
    if (pillSize === "defaultAlways") {
      return cn(pillTextShell, tone)
    }
    return cn("hidden md:inline-flex", pillTextShell, tone)
  }

  if (variant === "footerText") {
    return cn(
      "text-sm text-gray-600 hover:text-brand-500 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 rounded-sm",
    )
  }

  if (variant === "portalNav") {
    return cn(
      "flex items-center text-left w-full px-3 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2",
      isActive ? "bg-brand-100 font-semibold text-brand-500" : "text-gray-700 hover:bg-gray-50",
    )
  }

  if (variant === "portalNavLogout") {
    return cn(
      "w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-red-50 hover:text-brand-500 transition-colors cursor-pointer text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2",
    )
  }

  if (variant === "ctaPrimary") {
    return cn(
      "inline-flex items-center justify-center gap-2 px-6 py-3 bg-brand-500 text-white text-sm font-bold rounded-lg hover:bg-brand-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-brand-500",
    )
  }

  if (variant === "ctaOutline") {
    return cn(
      "inline-flex items-center gap-2 px-6 py-3 border border-brand-500 text-brand-500 text-sm font-semibold rounded-lg hover:bg-brand-500 hover:text-white transition-all whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2",
    )
  }

  if (variant === "ctaOutlineCompact") {
    return cn(
      "inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border border-brand-500 text-brand-500 hover:bg-brand-500 hover:text-white transition-colors cursor-pointer whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2",
    )
  }

  if (variant === "ctaOutlineIcon") {
    return cn(brandIconButtonShell, pillOutlineDefault, "disabled:opacity-50 disabled:pointer-events-none")
  }

  if (variant === "ctaOutlineSm") {
    return cn(
      "inline-flex items-center justify-center px-3 py-1.5 rounded-lg text-xs font-semibold border border-brand-500 text-brand-500 hover:bg-brand-500 hover:text-white disabled:opacity-50 cursor-pointer whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2",
    )
  }

  if (variant === "ctaOutlineNeutralToolbar") {
    return cn(
      "inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border border-gray-200 text-gray-700 hover:border-brand-500 hover:text-brand-500 transition-colors cursor-pointer whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2",
    )
  }

  if (variant === "ctaDanger") {
    return cn(
      "inline-flex items-center justify-center gap-2 px-6 py-3 bg-red-600 text-white text-sm font-bold rounded-lg hover:bg-red-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-red-600 disabled:opacity-50 disabled:cursor-not-allowed",
    )
  }

  if (variant === "ctaDangerOutline") {
    return cn(
      "inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-red-200 bg-red-50 text-red-800 hover:bg-red-100 hover:border-red-300 transition-colors cursor-pointer whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 focus-visible:ring-offset-2 disabled:opacity-40 disabled:pointer-events-none disabled:hover:border-red-200 disabled:hover:bg-red-50 disabled:hover:text-red-800",
    )
  }

  if (variant === "ctaOnDark") {
    return cn(
      "relative block px-8 py-4 bg-white text-brand-500 text-sm font-bold rounded-lg hover:bg-[#F5E6D3] transition-colors whitespace-nowrap text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-brand-500 cta-glow",
    )
  }

  if (variant === "linkSubtle") {
    return cn(
      "flex min-w-0 items-center gap-2 text-sm font-semibold text-gray-600 hover:text-brand-500 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 rounded-lg",
    )
  }

  return ""
}

export const ButtonLink = (props: ButtonLinkProps) => {
  const {
    variant,
    children,
    className,
    headerTone = "solid",
    isActive = false,
    pillSize = "default",
    ...rest
  } = props

  const resolved = resolveClasses({ variant, headerTone, isActive, pillSize })
  const merged = cn(resolved, className)

  if ("to" in rest && typeof rest.to === "string") {
    const { to, ...linkProps } = rest as ButtonLinkAsRouterLink
    return (
      <Link to={to} className={merged} {...linkProps}>
        {children}
      </Link>
    )
  }

  const buttonProps = rest as ButtonLinkAsNativeButton
  const { type = "button", ...btnRest } = buttonProps
  return (
    <button type={type} className={merged} {...btnRest}>
      {children}
    </button>
  )
}
